import { useTheme } from "@mui/material";
import { useMemo } from "react";
import { mapToGoalComposition } from "web_myplann/Goals/Helpers/mapToGoalComposition";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export type CompositionSlice = {
	id: string;
	value: number;
	label: string;
	color: string;
};

export type GoalDetailComposition = {
	slices: CompositionSlice[];
	coveredPercent: number;
};

export const useGoalDetailComposition = (
	goal: GoalDetail,
): GoalDetailComposition | null => {
	const { t } = useResource();
	const { palette } = useTheme();

	return useMemo<GoalDetailComposition | null>(() => {
		const composition = mapToGoalComposition(goal);
		if (!composition) return null;

		return {
			coveredPercent: composition.coveredPercent,
			slices: [
				{
					id: "own",
					value: composition.ownFunds,
					label: t(Resources.Goals.Detail.Composition.ownFunds),
					color: palette.blue[300],
				},
				{
					id: "loan",
					value: composition.loanAmount,
					label: t(Resources.Goals.Detail.Composition.loan),
					color: palette.amber[600],
				},
				{
					id: "invest",
					value: composition.coveredAmount,
					label: t(Resources.Goals.Detail.Composition.savings),
					color: palette.indigo[300],
				},
				{
					id: "remaining",
					value: composition.remaining,
					label: t(Resources.Goals.Detail.Composition.remaining),
					color: palette.grey[200],
				},
			].filter((slice) => slice.value > 0),
		};
	}, [goal, t, palette]);
};
